import { useEffect, useRef, useState } from "react";
import Card from "./ui/Card.jsx";
import Badge from "./ui/Badge.jsx";
import Button from "./ui/Button.jsx";
import ProgressBar from "./ui/ProgressBar.jsx";
import { getProviderMeta } from "../utils/providerContext.js";

const NAME_MAX = 100;

function formatShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function statusVariant(st) {
  const s = String(st || "").toLowerCase();
  if (s === "active") return "active";
  if (s === "complete" || s === "completed") return "complete";
  if (s === "paused") return "paused";
  if (s === "archived") return "archived";
  return "info";
}

export default function ProjectCard({
  project,
  onResume,
  onView,
  onDelete,
  onRenamed,
  sessionActive = false,
}) {
  const name = project?.orgName || project?.name || "Project";
  const url = project?.orgUrl || "";
  const status = project?.status || "active";
  const providerId = String(project?.providerId || "okta").toLowerCase();
  const providerLabel = getProviderMeta(providerId).projectHeaderPrefix;
  const rp = project?.roadmapProgress || {};
  const complete = Number(rp.complete) || 0;
  const total = Number(rp.total) || 0;
  const pct = total > 0 ? (complete / total) * 100 : 0;
  const st = String(status).toLowerCase();
  const projectId = project?.projectId;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(name);
  }, [name, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const cancelEdit = () => {
    setEditing(false);
    setDraft(name);
    setError("");
  };

  const saveRename = async () => {
    if (!projectId || saving) return;
    const next = String(draft || "").trim();
    if (!next) {
      setError("Name cannot be empty");
      return;
    }
    if (next === name) {
      cancelEdit();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/name`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: next.slice(0, NAME_MAX) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.project) {
        setError(data?.error || "Could not rename");
        return;
      }
      setEditing(false);
      onRenamed?.(data.project);
    } catch {
      setError("Could not rename");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "12px",
          marginBottom: "8px",
        }}
      >
        {editing ? (
          <div style={{ flex: 1, minWidth: 0 }}>
            <input
              ref={inputRef}
              value={draft}
              maxLength={NAME_MAX}
              disabled={saving}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void saveRename();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              aria-label="Project name"
              style={{
                width: "100%",
                fontWeight: 600,
                fontSize: 14,
                color: "var(--text-primary)",
                background: "var(--bg-elevated)",
                border: `1px solid ${error ? "var(--critical)" : "var(--border)"}`,
                borderRadius: 6,
                padding: "6px 8px",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
              <Button variant="primary" size="sm" onClick={() => void saveRename()} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving}>
                Cancel
              </Button>
              {error ? <span style={{ fontSize: 11, color: "var(--critical)" }}>{error}</span> : null}
            </div>
          </div>
        ) : (
          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{name}</span>
        )}
        <span style={{ display: "inline-flex", gap: "6px", flexShrink: 0 }}>
          <Badge variant="info">{providerLabel}</Badge>
          <Badge variant={statusVariant(status)}>{status}</Badge>
        </span>
      </div>
      <div className="font-mono text-secondary" style={{ fontSize: "12px", marginBottom: "12px", wordBreak: "break-all" }}>
        {url || "—"}
      </div>
      <div className="text-dim" style={{ fontSize: "11px", marginBottom: "16px" }}>
        Started {formatShort(project?.createdAt)} · Last session {formatShort(project?.updatedAt)}
      </div>
      {total > 0 ? (
        <div style={{ marginBottom: "16px" }}>
          <ProgressBar value={pct} label={`Roadmap: ${complete}/${total} complete`} showPercent />
        </div>
      ) : null}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "space-between" }}>
        <span style={{ display: "inline-flex", gap: "8px", flexWrap: "wrap" }}>
          {st === "archived" ? (
            <Badge variant="archived">Archived</Badge>
          ) : st === "complete" ? (
            <Button variant="secondary" size="sm" onClick={onView}>
              View Report →
            </Button>
          ) : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <Button variant="primary" size="sm" onClick={onResume}>
                Resume →
              </Button>
              {sessionActive ? (
                <span
                  title="Token active — no re-entry needed"
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "var(--healthy)",
                    flexShrink: 0,
                  }}
                  aria-label="Session active"
                />
              ) : null}
            </span>
          )}
        </span>
        <span style={{ display: "inline-flex", gap: "4px", flexWrap: "wrap" }}>
          {!editing && typeof onRenamed === "function" ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDraft(name);
                setError("");
                setEditing(true);
              }}
              title="Rename project"
            >
              Rename
            </Button>
          ) : null}
          {typeof onDelete === "function" ? (
            <Button variant="ghost" size="sm" onClick={onDelete} title="Delete project">
              Delete
            </Button>
          ) : null}
        </span>
      </div>
    </Card>
  );
}
